import { Setter } from 'jotai';
import { useAtomCallback } from 'jotai/utils';
import { debounce } from 'lodash-es';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router';
import { useLoadingBarState } from '../../../components/LoadingBar/LoadingBarContext';
import { useToastMessageState } from '../../../components/ToastMessage/ToastMessageProvider';
import { ROUTES } from '../../../constants/RouteNameMapping';
import {
  CaptableSource,
  ICaptableDataModel,
  StakeholderTBDName,
} from '../../../data-models/captable2.data-model';
import {
  createCaptable,
  deleteCaptable,
  duplicateCaptable,
  makeCaptablePrimary,
  updateCaptable,
  updatePrimaryCaptableSource,
} from '../../../services/queries/MaggieCapTableQueries';
import { getErrorMessage } from '../../../services/queryHelpers';
import {
  clientInvestmentsStateJ,
  otherInvestmentsStateJ,
  shareClassesStateJ,
} from '../../../services/state/CapTableFormStateJ';
import { LoadingId } from '../../../types';
import { MaggieFeatureFlags } from '../../../util/feature-flags';
import { fromCaptableDataModel } from '../../../view-models/captable.view-model';
import { IOtherInvestmentsGridRow } from './InvestmentGridDefs';
import { useInvalidateCaptables } from './useInvalidateCaptables';
import { CaptableIdParam } from './useSyncCaptableFormFromPath';

export function usePreviewCaptable() {
  const { pushErrorToast } = useToastMessageState();

  return useCallback(
    async (payload: Partial<ICaptableDataModel>) => {
      if (!payload.companyId) {
        pushErrorToast({ message: 'Missing company ID' });
        return;
      }
      try {
        const data = await createCaptable(payload, true);
        return data;
      } catch (err) {
        console.error(err);
        const message = getErrorMessage(err, 'Failed to preview cap table');
        pushErrorToast({ message });
      }
    },
    [pushErrorToast]
  );
}

interface IActionOptions {
  showLoadingBar?: boolean;
}

export function useCaptableActions() {
  const { pushErrorToast, pushSuccessToast } = useToastMessageState();
  const invalidateCaptables = useInvalidateCaptables();
  const {
    actions: { startLoading, stopLoading },
  } = useLoadingBarState();

  const createCaptableHandler = useCallback(
    async (payload: Partial<ICaptableDataModel>, options: IActionOptions = {}) => {
      if (!payload.companyId) {
        pushErrorToast({ message: 'Missing company ID' });
        return;
      }
      try {
        if (options.showLoadingBar) {
          startLoading(LoadingId.createCaptable);
        }
        const data = await createCaptable(payload, false);
        // Key off the response's company, as every other handler does, so a trusted id can never be
        // written to another company's selection.
        // Await so the cap-table page remounts against refreshed state with the new id selected.
        await invalidateCaptables(data.companyId, data, { trustId: true });
        return data;
      } catch (err) {
        console.error(err);
        const message = getErrorMessage(err, 'Failed to create cap table');
        pushErrorToast({ message });
      } finally {
        if (options.showLoadingBar) {
          stopLoading(LoadingId.createCaptable);
        }
      }
    },
    [invalidateCaptables, pushErrorToast, startLoading, stopLoading]
  );

  const updateCaptableHandler = useCallback(
    async (payload: Partial<ICaptableDataModel>, options: IActionOptions = {}) => {
      if (!payload.id || !payload.companyId) {
        pushErrorToast({ message: 'Missing id or companyId' });
        return;
      }
      try {
        if (options.showLoadingBar) {
          startLoading(LoadingId.updateCaptable);
        }
        const data = await updateCaptable(payload);
        // No trustId: the edited source already has a timeline, so re-anchoring on (source, type)
        // lands correctly, and a PUT response absorbed into an existing segment must not be pinned.
        invalidateCaptables(payload.companyId, data);
        pushSuccessToast({ message: 'Cap table updated successfully' });
        return data;
      } catch (err) {
        console.error(err);
        const message = getErrorMessage(err, 'Failed to update cap table');
        pushErrorToast({ message });
      } finally {
        if (options.showLoadingBar) {
          stopLoading(LoadingId.updateCaptable);
        }
      }
    },
    [invalidateCaptables, pushErrorToast, pushSuccessToast, startLoading, stopLoading]
  );

  const debouncedUpdateCaptableHandler = useMemo(
    () => debounce(updateCaptableHandler, 1000),
    [updateCaptableHandler]
  );

  const deleteCaptableHandler = useCallback(
    async (captable: ICaptableDataModel) => {
      try {
        await deleteCaptable(captable.id);
        invalidateCaptables(captable.companyId);
      } catch (err) {
        console.error(err);
        const message = getErrorMessage(err, 'Failed to delete cap table');
        pushErrorToast({ message });
      }
    },
    [invalidateCaptables, pushErrorToast]
  );

  return {
    createCaptableHandler,
    updateCaptableHandler,
    deleteCaptableHandler,
    debouncedUpdateCaptableHandler,
  };
}

export function useEditCaptable() {
  const navigate = useNavigate();

  return useCallback(
    (captable: ICaptableDataModel) => {
      const { id } = captable;

      navigate(`${ROUTES.CAP_TABLE_FORM}?${CaptableIdParam}=${id}`);
    },
    [navigate]
  );
}

export function useSetCaptableFormState() {
  return useAtomCallback(
    useCallback((_get, set: Setter, captable: ICaptableDataModel) => {
      const viewModel = fromCaptableDataModel(captable);
      set(shareClassesStateJ, viewModel._viewModel.shareClasses);
      set(clientInvestmentsStateJ, viewModel._viewModel.clientInvestments);
      set(otherInvestmentsStateJ, filterOutCalculatedInvestments(viewModel._viewModel.otherInvestments));
    }, [])
  );
}

function filterOutCalculatedInvestments(investments: IOtherInvestmentsGridRow[]) {
  return investments.filter((i) => i.investor !== StakeholderTBDName);
}

export function useMakeCaptablePrimaryLegacy() {
  const { pushErrorToast } = useToastMessageState();
  const invalidateCaptables = useInvalidateCaptables();

  return useCallback(
    async (id: number) => {
      try {
        const data = await makeCaptablePrimary(id);
        invalidateCaptables(data.companyId, data);
      } catch (err) {
        console.error(err);
        const message = getErrorMessage(err, 'Failed to make cap table primary');
        pushErrorToast({ message });
      }
    },
    [invalidateCaptables, pushErrorToast]
  );
}

export function useMakeCaptablePrimary() {
  const { pushErrorToast } = useToastMessageState();
  const invalidateCaptables = useInvalidateCaptables();

  return useCallback(
    async (companyId: number, source: CaptableSource) => {
      try {
        await updatePrimaryCaptableSource(companyId, source);
        invalidateCaptables(companyId);
      } catch (err) {
        console.error(err);
        const message = getErrorMessage(err, 'Failed to make cap table primary');
        pushErrorToast({ message });
      }
    },
    [invalidateCaptables, pushErrorToast]
  );
}

// we need to await both delete and make primary before invalidating the cache, hence the separate hook
export function useMakeOtherPrimaryAndDeleteLegacy() {
  const { pushErrorToast } = useToastMessageState();
  const invalidateCaptables = useInvalidateCaptables();

  return useCallback(
    async (newPrimaryId: number, toDeleteId: number) => {
      try {
        const data = await makeCaptablePrimary(newPrimaryId);
        await deleteCaptable(toDeleteId);
        invalidateCaptables(data.companyId, data);
      } catch (err) {
        console.error(err);
        const message = getErrorMessage(err, 'Failed to update/delete cap table');
        pushErrorToast({ message });
      }
    },
    [invalidateCaptables, pushErrorToast]
  );
}

export function useMakeOtherPrimaryAndDelete() {
  const { pushErrorToast } = useToastMessageState();
  const invalidateCaptables = useInvalidateCaptables();

  return useCallback(
    async (companyId: number, newPrimarySource: CaptableSource, toDeleteId: number) => {
      try {
        await updatePrimaryCaptableSource(companyId, newPrimarySource);
        await deleteCaptable(toDeleteId);
        invalidateCaptables(companyId);
      } catch (err) {
        console.error(err);
        const message = getErrorMessage(err, 'Failed to update/delete cap table');
        pushErrorToast({ message });
      }
    },
    [invalidateCaptables, pushErrorToast]
  );
}

export function useDuplicateCaptable() {
  const { pushErrorToast } = useToastMessageState();
  const invalidateCaptables = useInvalidateCaptables();
  const { asOfCapTables } = useFlags<MaggieFeatureFlags>();

  return useCallback(
    async (captableId: number) => {
      try {
        const data = await duplicateCaptable(captableId, asOfCapTables ? { version: 2 } : undefined);
        invalidateCaptables(data.companyId, data, { trustId: true });
      } catch (err) {
        console.error(err);
        const message = getErrorMessage(err, 'Failed to duplicate cap table');
        pushErrorToast({ message });
      }
    },
    [invalidateCaptables, pushErrorToast, asOfCapTables]
  );
}
